// apps/web/src/features/cargo/components/CargoPaymentManagerView.tsx
//
// Manager projection of the client-payment drawer (#1938). The manager picks a
// DESIRED status (paid | partially_paid — never a direct final status) and
// submits it for financier review. Submit needs ≥1 active document AND a chosen
// status. The desired-status control is editable only while review is open
// (heuristic below); the server is authoritative and a 409 surfaces inline.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorCode, type CargoClientPaymentSubmitBody } from '@magimp/shared';

import { Button, Select } from '../../../ui/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { ApiClientError, errorMessage } from '../../../api/errors.js';
import { useSubmitCargoClientPayment } from '../mutations.js';

type RequestedStatus = CargoClientPaymentSubmitBody['requestedStatus'];

export interface CargoPaymentManagerViewProps {
  cargoId: string;
  paidByClientStatus: string;
  hasActiveDocument: boolean;
  onDirtyChange: (dirty: boolean) => void;
  onBusyChange: (busy: boolean) => void;
  onSubmitted: () => void;
}

const REQUESTED_STATUSES: readonly RequestedStatus[] = ['paid', 'partially_paid'];

export function CargoPaymentManagerView({
  cargoId,
  paidByClientStatus,
  hasActiveDocument,
  onDirtyChange,
  onBusyChange,
  onSubmitted,
}: CargoPaymentManagerViewProps) {
  const { t } = useTranslation(['cargo']);
  const showToast = useToastStore((s) => s.show);
  const submit = useSubmitCargoClientPayment(cargoId);
  const [desired, setDesired] = useState<RequestedStatus | ''>('');
  const [inlineError, setInlineError] = useState<string | null>(null);

  // ponytail: best-effort client gate. Review is open only while `under_review`;
  // any committed status (paid/partially_paid/not_paid) locks the control until
  // a document add/delete reopens review server-side. The 409
  // CARGO_PAYMENT_REVIEW_NOT_OPEN backstop is authoritative.
  const reviewOpen = paidByClientStatus === 'under_review' || paidByClientStatus === 'not_paid';

  useEffect(() => onDirtyChange(desired !== ''), [desired, onDirtyChange]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);
  useEffect(() => onBusyChange(submit.isPending), [submit.isPending, onBusyChange]);
  useEffect(() => () => onBusyChange(false), [onBusyChange]);

  const options = REQUESTED_STATUSES.map((value) => ({
    value,
    label: t(`cargo:payment.manager.options.${value}`),
  }));

  const submitDisabled = submit.isPending || !hasActiveDocument || desired === '' || !reviewOpen;

  function onSubmit() {
    if (desired === '') return;
    setInlineError(null);
    submit.mutate(desired, {
      onSuccess: () => {
        showToast({ tone: 'success', text: t('cargo:payment.manager.toasts.submitted') });
        setDesired('');
        onSubmitted();
      },
      onError: (err) => {
        if (err instanceof ApiClientError && err.code === ErrorCode.CARGO_PAYMENT_REVIEW_NOT_OPEN) {
          setInlineError(t('cargo:payment.errors.reviewNotOpen'));
          return;
        }
        if (
          err instanceof ApiClientError &&
          err.code === ErrorCode.CARGO_PAYMENT_NO_ACTIVE_DOCUMENT
        ) {
          setInlineError(t('cargo:payment.errors.noActiveDocument'));
          return;
        }
        showToast({ tone: 'error', text: errorMessage(err) });
      },
    });
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span style={{ fontSize: 12, color: 'var(--fg-2)' }}>
        {t('cargo:payment.manager.desiredStatusLabel')}
      </span>
      <div data-testid="cargo-payment-desired-status">
        <Select
          value={desired}
          onChange={(v) => setDesired(v as RequestedStatus)}
          options={options}
          placeholder={t('cargo:payment.manager.desiredStatusPlaceholder')}
          disabled={!reviewOpen}
          style={{ width: '100%' }}
        />
      </div>
      {!reviewOpen && (
        <p
          data-testid="cargo-payment-locked-hint"
          style={{ margin: 0, fontSize: 12, color: 'var(--fg-3)' }}
        >
          {t('cargo:payment.manager.lockedHint')}
        </p>
      )}
      {reviewOpen && !hasActiveDocument && (
        <p
          role="alert"
          data-testid="cargo-payment-no-doc-warning"
          style={{ margin: 0, fontSize: 12, color: 'var(--status-warn-fg)' }}
        >
          {t('cargo:payment.noDocumentWarning')}
        </p>
      )}
      {inlineError && (
        <p
          role="alert"
          data-testid="cargo-payment-inline-error"
          style={{ margin: 0, fontSize: 12, color: 'var(--status-error-fg)' }}
        >
          {inlineError}
        </p>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          kind="primary"
          onClick={onSubmit}
          disabled={submitDisabled}
          data-testid="cargo-payment-submit"
        >
          {t('cargo:payment.manager.submit')}
        </Button>
      </div>
    </div>
  );
}
